import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Button,
    Stack,
    Chip,
    Box,
} from "@mui/material";

export default function SauvegardeModal({
    open,
    onClose,
    onSave,
    initialData,
    clients,
    loading = false,
}) {
    const [form, setForm] = useState({
        name: "",
        clientId: "",
        expectedEmailCount: 1,
        windowStart: "00:00",
        windowEnd: "23:59",
        expectedSenders: [],
        subjectPattern: "",
        successKeywords: [],
        failureKeywords: [],
        alertKeywords: [],
    });

    const [senderInput, setSenderInput] = useState("");
    const [successKeywordInput, setSuccessKeywordInput] = useState("");
    const [failureKeywordInput, setFailureKeywordInput] = useState("");
    const [alertKeywordInput, setAlertKeywordInput] = useState("");

    useEffect(() => {
        if (initialData) {
            setForm({
                name: initialData.name || "",
                clientId: initialData.clientId || "",
                expectedEmailCount: initialData.expectedEmailCount || 1,
                windowStart: initialData.windowStart || "00:00",
                windowEnd: initialData.windowEnd || "23:59",
                expectedSenders: initialData.expectedSenders || [],
                subjectPattern: initialData.subjectPattern || "",
                successKeywords: initialData.successKeywords || [],
                failureKeywords: initialData.failureKeywords || [],
                alertKeywords: initialData.alertKeywords || [],
            });
        } else {
            setForm({
                name: "",
                clientId: "",
                expectedEmailCount: 1,
                windowStart: "00:00",
                windowEnd: "23:59",
                expectedSenders: [],
                subjectPattern: "",
                successKeywords: [],
                failureKeywords: [],
                alertKeywords: [],
            });
        }
        setSenderInput("");
        setSuccessKeywordInput("");
        setFailureKeywordInput("");
        setAlertKeywordInput("");
    }, [initialData, open]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const addSender = () => {
        if (senderInput.trim() && !form.expectedSenders.includes(senderInput)) {
            setForm((prev) => ({
                ...prev,
                expectedSenders: [...prev.expectedSenders, senderInput],
            }));
            setSenderInput("");
        }
    };

    const removeSender = (email) => {
        setForm((prev) => ({
            ...prev,
            expectedSenders: prev.expectedSenders.filter((s) => s !== email),
        }));
    };

    const addKeyword = (type) => {
        const input =
            type === "success"
                ? successKeywordInput
                : type === "failure"
                  ? failureKeywordInput
                  : alertKeywordInput;

        if (input.trim()) {
            const field =
                type === "success"
                    ? "successKeywords"
                    : type === "failure"
                      ? "failureKeywords"
                      : "alertKeywords";

            setForm((prev) => ({
                ...prev,
                [field]: [...(prev[field] || []), input],
            }));

            if (type === "success") setSuccessKeywordInput("");
            else if (type === "failure") setFailureKeywordInput("");
            else setAlertKeywordInput("");
        }
    };

    const removeKeyword = (type, keyword) => {
        const field =
            type === "success"
                ? "successKeywords"
                : type === "failure"
                  ? "failureKeywords"
                  : "alertKeywords";

        setForm((prev) => ({
            ...prev,
            [field]: prev[field].filter((k) => k !== keyword),
        }));
    };

    const handleSave = () => {
        if (!form.name || !form.clientId) {
            alert("Veuillez remplir le nom et le client");
            return;
        }

        onSave(form);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {initialData
                    ? "Modifier la sauvegarde"
                    : "Ajouter une sauvegarde"}
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={2}>
                    {/* Ligne 1: Nom, Client */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Nom de la sauvegarde"
                            required
                            value={form.name}
                            onChange={(e) =>
                                handleChange("name", e.target.value)
                            }
                            placeholder="ex: Backup Daily SQL"
                        />
                        <TextField
                            fullWidth
                            select
                            label="Client"
                            required
                            value={form.clientId}
                            onChange={(e) =>
                                handleChange("clientId", e.target.value)
                            }
                        >
                            <MenuItem value="">Sélectionner un client</MenuItem>
                            {clients.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                    {c.nom || c.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    {/* Ligne 2: Emails attendus */}

                    <TextField
                        fullWidth
                        type="number"
                        label="Nombre d'emails attendus"
                        value={form.expectedEmailCount}
                        onChange={(e) =>
                            handleChange(
                                "expectedEmailCount",
                                parseInt(e.target.value),
                            )
                        }
                        inputProps={{ min: 1 }}
                    />

                    {/* Ligne 3: Fenêtre de vérification */}
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            label="Début fenêtre (HH:mm)"
                            type="time"
                            value={form.windowStart}
                            onChange={(e) =>
                                handleChange("windowStart", e.target.value)
                            }
                            inputProps={{ step: 300 }}
                        />
                        <TextField
                            label="Fin fenêtre (HH:mm)"
                            type="time"
                            value={form.windowEnd}
                            onChange={(e) =>
                                handleChange("windowEnd", e.target.value)
                            }
                            inputProps={{ step: 300 }}
                        />
                    </Stack>

                    {/* Pattern de sujet */}
                    <TextField
                        fullWidth
                        label="Pattern de sujet (regex optionnel)"
                        value={form.subjectPattern}
                        onChange={(e) =>
                            handleChange("subjectPattern", e.target.value)
                        }
                        placeholder="ex: ^Backup.*SQL$"
                        multiline
                        rows={2}
                    />

                    {/* Expéditeurs */}
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Ajouter un expéditeur"
                                value={senderInput}
                                onChange={(e) => setSenderInput(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addSender();
                                    }
                                }}
                                placeholder="backup@system.com"
                            />
                            <Button onClick={addSender} variant="outlined">
                                Ajouter
                            </Button>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            {form.expectedSenders.map((sender) => (
                                <Chip
                                    key={sender}
                                    label={sender}
                                    onDelete={() => removeSender(sender)}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* Keywords - Success */}
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Keyword de succès"
                                value={successKeywordInput}
                                onChange={(e) =>
                                    setSuccessKeywordInput(e.target.value)
                                }
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addKeyword("success");
                                    }
                                }}
                                placeholder="ex: Success, Completed"
                            />
                            <Button
                                onClick={() => addKeyword("success")}
                                variant="outlined"
                                color="success"
                            >
                                Ajouter
                            </Button>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            {form.successKeywords?.map((kw) => (
                                <Chip
                                    key={kw}
                                    label={kw}
                                    color="success"
                                    variant="outlined"
                                    onDelete={() =>
                                        removeKeyword("success", kw)
                                    }
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* Keywords - Failure */}
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Keyword d'erreur"
                                value={failureKeywordInput}
                                onChange={(e) =>
                                    setFailureKeywordInput(e.target.value)
                                }
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addKeyword("failure");
                                    }
                                }}
                                placeholder="ex: Failed, Error"
                            />
                            <Button
                                onClick={() => addKeyword("failure")}
                                variant="outlined"
                                color="error"
                            >
                                Ajouter
                            </Button>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            {form.failureKeywords?.map((kw) => (
                                <Chip
                                    key={kw}
                                    label={kw}
                                    color="error"
                                    variant="outlined"
                                    onDelete={() =>
                                        removeKeyword("failure", kw)
                                    }
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* Keywords - Alert */}
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Keyword d'alerte"
                                value={alertKeywordInput}
                                onChange={(e) =>
                                    setAlertKeywordInput(e.target.value)
                                }
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addKeyword("alert");
                                    }
                                }}
                                placeholder="ex: Warning, Alert"
                            />
                            <Button
                                onClick={() => addKeyword("alert")}
                                variant="outlined"
                                color="warning"
                            >
                                Ajouter
                            </Button>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            {form.alertKeywords?.map((kw) => (
                                <Chip
                                    key={kw}
                                    label={kw}
                                    color="warning"
                                    variant="outlined"
                                    onDelete={() => removeKeyword("alert", kw)}
                                />
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annuler</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading}
                >
                    {initialData ? "Mettre à jour" : "Créer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
